@if (useAccordionComponent) {
  <mat-accordion>
    <mat-expansion-panel
      (opened)="panelOpenState.set(true)"
      (closed)="panelOpenState.set(false)"
      [expanded]="toggleAccordion"
      togglePosition="before"
      class="border-2">
      <mat-expansion-panel-header class="min-h-[100px] bg-brandLightBlueFill dark:bg-gray-700">
        <div class="flex flex-row justify-between items-center w-full">
          <div class="flex flex-col">
            <span class="font-bold text-xl dark:text-white">{{ item?.data?.quote_request_bond?.company_name }}</span>
            @if (item?.data?.company_dba) {
              <span class="font-semibold text-md">DBA: {{ item?.data?.company_dba }}</span>
            }
            <span class="font-medium text-sm text-gray-400 dark:text-gray-400">
              {{ item?.data?.quote_request_bond?.state_abbreviation }} -
              {{ item?.data?.quote_request_bond?.surety_bond_label }}
              @if (item?.data?.selected_quote?.bond_effective_date) {
                | Effective Date: {{ item?.data?.selected_quote?.bond_effective_date | date: 'shortDate' }}
              }
            </span>
          </div>
          <div class="flex flex-col align-right text-right">
            <p class="text-lg font-bold" *ngIf="item?.data?.selected_quote?.premium">
              {{ item?.data?.selected_quote?.premium | currency: 'USD' }}
            </p>

            <p class="accent-green-500 text-sm text-primary" *ngIf="item?.data?.selected_quote?.duration">
              {{ getDurationTerm(item.data) }}
            </p>
          </div>
        </div>
      </mat-expansion-panel-header>
      <div class="rounded-lg px-2">
        <div class="w-full flex flex-col justify-between pt-4">
          <!--Bond Details-->
          <div class="flex flex-row justify-between">
            <span class="bond-details-group-title">Bond Details</span>
            <button mat-icon-button [matMenuTriggerFor]="menu" aria-label="Bond Details Menu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button
                mat-menu-item
                *ngFor="let _item of menuItems"
                [disabled]="!_item.enabled"
                (click)="onMenuItemClick(_item)">
                <mat-icon *ngIf="_item.icon">{{ _item.icon }}</mat-icon>
                <span>{{ _item.label }}</span>
              </button>
            </mat-menu>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <!-- Order Number -->
            <div>
              <p class="bond-details-displayed-label">Order #</p>
              <p class="bond-details-displayed-value">{{ getOrderNumber() }}</p>
            </div>

            <!-- Date Ordered -->
            <div>
              <p class="bond-details-displayed-label">Requested</p>
              <p class="bond-details-displayed-value">{{ getQuoteRequestDate() | date: 'MM/dd/yyyy' }}</p>
            </div>

            <!-- Company Type -->
            <div>
              <p class="bond-details-displayed-label">Company Type</p>
              <p class="bond-details-displayed-value">{{ getCompanyType() }}</p>
            </div>

            <!-- Principal Address -->
            <div>
              <p class="bond-details-displayed-label">Principal Address</p>
              <p class="bond-details-displayed-value">
                {{ item?.data?.principal_address ? getFormattedAddress(item?.data?.principal_address) : 'N/A' }}
              </p>
            </div>
          </div>
          <mat-divider class="bond-details-mat-divider"></mat-divider>
          <!--Bond Owner-->
          <div class="flex flex-col">
            <div class="flex flex-row justify-between">
              <span class="bond-details-group-title">Customer</span>
              @if (reminderRecipients().length > 1 && (+item.data.customer_can_purchase === 1 || isOverdue())) {
                <div class="flex flex-col items-end gap-2 mt-2">
                  <button
                    [matMenuTriggerFor]="reminderRecipientsMenu"
                    aria-label="Reminder"
                    class="cursor-pointer flex flex-row items-center justify-between gap-2 text-primary text-[10px] border-2 px-2 border-primary rounded-lg">
                    Remind Customers
                  </button>
                  <mat-menu #reminderRecipientsMenu="matMenu">
                    @for (recipient of reminderRecipients(); track recipient.user_id) {
                      <button mat-menu-item (click)="openSendReminderDialog(recipient)">
                        <div class="flex flex-col">
                          <p class="font-semibold break-words">{{ recipient.name }}</p>
                          <div
                            class="text-xs text-gray-500 break-words"
                            *ngIf="recipient.email && !isMissingEmail(recipient.email)">
                            {{ recipient.email | ellipsis: 40 }}
                          </div>
                        </div>
                      </button>
                    }
                  </mat-menu>
                </div>
              }
            </div>
            <div class="bond-details-group-content">
              <div class="flex flex-col justify-between items-start w-full">
                @if (reminderRecipients().length === 1 && (+item.data.customer_can_purchase === 1 || isOverdue())) {
                  <div class="flex flex-row justify-between w-full">
                    <div class="bond-details-bond-label">
                      {{ (item?.data?.user_first_name || '') + ' ' + (item?.data?.user_last_name || '') }}
                    </div>
                    <div class="flex flex-col items-end">
                      <a
                        [matTooltip]="'Send a reminder'"
                        class="w-[100px] h-5 cursor-pointer flex flex-row items-center justify-center text-primary text-[10px] border-2 px-2 border-primary rounded-lg"
                        (click)="openSendReminderDialog(reminderRecipients()[0])">
                        Send Reminder
                      </a>
                    </div>
                  </div>
                } @else {
                  <div class="bond-details-bond-label">
                    {{ (item?.data?.user_first_name || '') + ' ' + (item?.data?.user_last_name || '') }}
                  </div>
                }
                @if (item.data.user_email_address && !isServiceEmail(item.data.user_email_address)) {
                  <p class="bond-details-displayed-value break-words">{{ item.data.user_email_address }}</p>
                }
                <p class="bond-details-displayed-value">{{ item?.data?.user_phone }}</p>
              </div>
            </div>
          </div>
          <mat-divider class="bond-details-mat-divider"></mat-divider>

          @if (item?.data?.selected_quote) {
            <!--Quote Options-->
            <div class="cipx-form-section">
              <div class="cipx-form-label mt-0 pt-0">Quote Options</div>
              <div class="cipx-form-form-group">
                @if (+item?.data?.customer_can_purchase === 1 && item?.data?.quote_selection_form) {
                  <form [formGroup]="item?.data?.quote_selection_form!">
                    <!--Bond Option Selector-->
                    <mat-radio-group
                      aria-label="Select Bond Term"
                      class="cipx-form-mat-radio-group-rounded"
                      formControlName="selected_quote_option"
                      (change)="setSelectedQuoteOption()">
                      @for (quoteOption of item?.data?.quote_request_bond?.quotes; track $index) {
                        <mat-radio-button
                          [value]="quoteOption.premium"
                          class="cipx-full-width-radio"
                          [ngClass]="{
                            'cipx-full-width-radio-selected':
                              quoteOption.premium === item?.data?.selected_quote?.premium,
                          }">
                          <div class="cipx-full-width-radio-label">
                            <div class="flex flex-col">
                              @if (getDurationTerm(quoteOption)) {
                                <span>{{ getDurationTerm(quoteOption) }}</span>
                              }
                              <div class="flex flex-row justify-between text-sm text-gray-400 dark:text-white">
                                <span>{{ getDateRangeString() }}</span>
                              </div>
                            </div>
                            <div class="flex flex-col align-right text-right">
                              <span class="font-bold">
                                {{ quoteOption?.premium | currency: 'USD' }}
                              </span>
                              @if (
                                item?.data?.selected_quote?.collateral !== null &&
                                item?.data?.selected_quote?.collateral !== undefined &&
                                !isNaN(+item?.data?.selected_quote?.collateral) &&
                                +item?.data?.selected_quote?.collateral > 0
                              ) {
                                <span class="text-red-500 dark:text-black text-sm">
                                  Requires Collateral: {{ item?.data?.selected_quote?.collateral | currency: 'USD' }}
                                </span>
                              }
                            </div>
                          </div>
                        </mat-radio-button>
                      }
                    </mat-radio-group>
                  </form>
                } @else {
                  <div class="flex flex-col gap-0 m-1">
                    <div
                      class="flex flex-row w-full rounded-md dark:text-white selected border-2 border-brandOrange bg-orange-50 dark:bg-brandOrange">
                      <mat-radio-button [checked]="true" class="pt-1 w-[30px]" [disabled]="true"></mat-radio-button>
                      <div class="flex flex-col w-full p-3">
                        <div class="flex flex-row justify-between text-md font-semibold">
                          <span>{{ getDurationTerm(item?.data?.selected_quote) }}</span>
                          <div class="flex flex-col justify-end items-end">
                            <span>{{ item?.data?.selected_quote?.premium | currency: 'USD' }}</span>
                          </div>
                        </div>
                        <div class="flex flex-row justify-between text-sm text-gray-400 dark:text-white">
                          <span>
                            {{ getDateRangeString() }}
                          </span>
                          @if (
                            item?.data?.selected_quote?.collateral !== null &&
                            item?.data?.selected_quote?.collateral !== undefined &&
                            !isNaN(+item?.data?.selected_quote?.collateral) &&
                            +item?.data?.selected_quote?.collateral > 0
                          ) {
                            <span class="text-red-500 dark:text-black text-sm">
                              Requires Collateral: {{ item?.data?.selected_quote?.collateral | currency: 'USD' }}
                            </span>
                          }
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          }
          <mat-divider class="bond-details-mat-divider"></mat-divider>
          <!--Bond Delivery-->
          <div class="flex flex-col">
            <span class="bond-details-group-title">Bond Delivery</span>
            @if (shouldShowShippingDropdown()) {
              <!-- Show dropdown when multiple shipping options are available -->
              <mat-form-field class="w-full mt-2" subscriptSizing="dynamic" hideRequiredMarker="true">
                <mat-label>Delivery Method</mat-label>
                <mat-select
                  [value]="getCurrentShippingOptionId()"
                  (selectionChange)="onShippingOptionChange($event.value)">
                  <!-- Show selectable shipping options -->
                  <mat-option *ngFor="let option of getSelectableShippingOptions()" [value]="option.shipping_option_id">
                    {{ option.shipping_option_label }}
                    <!-- @if (!option.shipping_option_user_selectable) {
                      <span class="text-blue-500 text-xs">(System Option)</span>
                    } -->
                    @if (option.shipping_option_price > 0) {
                      <span class="text-gray-500">- {{ option.shipping_option_price | currency: 'USD' }}</span>
                    }
                  </mat-option>
                </mat-select>

                <!-- Show informational hint -->
                @if (!isCurrentOptionUserSelectable()) {
                  <mat-hint class="text-xs text-gray-500">
                    You can change from the current system-assigned option or keep it
                  </mat-hint>
                } @else {
                  <mat-hint class="text-xs text-gray-500">Select your preferred delivery method</mat-hint>
                }
              </mat-form-field>
            } @else {
              <!-- Show static label when only one shipping option is available -->
              <p class="bond-details-displayed-label">Delivery Method</p>
              <span class="bg-orange-300 dark:bg-brandOrange dark:text-white px-4 py-1 rounded-md w-fit">
                {{ getShippingOption() }}
              </span>
            }
          </div>

          @if (hasAttachedFiles()) {
            <mat-divider class="bond-details-mat-divider"></mat-divider>
            <div class="flex flex-col">
              <span class="bond-details-group-title">
                Attached File{{ getAttachedFiles().length > 1 ? 's (' + getAttachedFiles().length + ')' : '' }}
              </span>
              <div class="bond-details-files-grid">
                <!--"-->
                <div
                  *ngFor="let file of getAttachedFiles()"
                  class="bond-details-file-container"
                  [title]="file.file_friendly_name">
                  <mat-icon class="bond-details-file-icon">
                    {{ getFileIcon(file.file_extension) }}
                  </mat-icon>
                  <div class="bond-details-file-labels-container">
                    <div class="bond-details-file-purpose-label">
                      {{ file.file_purpose_label }}
                    </div>
                    <div class="bond-details-file-friendly-name">
                      {{ file.file_friendly_name }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </mat-expansion-panel>
  </mat-accordion>
}
